import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { setBusinessUser } from "../redux/slices/businessSlice";
import { getBusinessUserProfileImageUrl } from "../Components/utils/buserimageUtils";

import {
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaCalendarCheck,
  FaCircle,
  FaUserMd,
  FaShieldAlt,
} from "react-icons/fa";

// ============================================================
// SAFE FALLBACK
// ============================================================

const safe = (value, fallback = "N/A") => {
  return value ?? fallback;
};

// ============================================================
// DESCRIPTION BLOCK
// ============================================================

const DescriptionBlock = ({ description }) => {
  const [showFull, setShowFull] = useState(false);

  if (!description) {
    return null;
  }

  const isLong = description.length > 150;

  const visibleDescription =
    showFull || !isLong ? description : `${description.slice(0, 150)}...`;

  return (
    <div className="uc-description">
      <span>{visibleDescription}</span>

      {isLong && (
        <button
          type="button"
          className="uc-read-more"
          onClick={() => setShowFull((previous) => !previous)}
        >
          {showFull ? "Show Less" : "Read More"}
        </button>
      )}
    </div>
  );
};

// ============================================================
// USER CARD
// ============================================================

const UserCard = ({ user }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ==========================================================
  // AUTHENTICATION
  // ==========================================================

  const token = useSelector((state) => state.auth.token);
  const isAuthenticated = Boolean(token);

  // ==========================================================
  // NORMALIZE USER DATA
  // ==========================================================

  const actualUser = useMemo(() => {
    return user?.user || user || {};
  }, [user]);

  // ==========================================================
  // ONLINE STATUS
  // ==========================================================

  const isOnline = actualUser?.isOnline === true;

  // ==========================================================
  // PROFILE IMAGE
  // ==========================================================

  const profileImage = useMemo(() => {
    return getBusinessUserProfileImageUrl(actualUser?.profilePic);
  }, [actualUser?.profilePic]);

  const fallbackImage = useMemo(() => {
    return getBusinessUserProfileImageUrl(null);
  }, []);

  // ==========================================================
  // BOOK APPOINTMENT
  // ==========================================================

  const handleBookAppointment = () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    dispatch(setBusinessUser(actualUser));

    navigate("/book-appointment", {
      state: {
        user: actualUser,
      },
    });
  };

  // ==========================================================
  // IMAGE ERROR
  // ==========================================================

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackImage) {
      event.currentTarget.src = fallbackImage;
    }
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <>
      <style>{`

        /* ====================================================
           ROOT CARD
        ==================================================== */

        .uc-card {
          position: relative;

          display: block;

          width: 100%;
          max-width: 100%;
          min-width: 0;

          overflow: hidden;

          box-sizing: border-box;

          border: 1px solid #f1f5f9;
          border-radius: clamp(12px, 1.5vw, 18px);

          background: #ffffff;

          box-shadow:
            0 clamp(3px, 0.5vw, 6px)
            clamp(10px, 1.5vw, 20px)
            rgba(0, 0, 0, 0.08);

          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
        }

        .uc-card:hover {
          transform: translateY(-3px);

          box-shadow:
            0 12px 30px rgba(0, 0, 0, 0.14);
        }


        /* ====================================================
           TOP STRIP
        ==================================================== */

        .uc-top-strip {
          width: 100%;
          height: clamp(4px, 0.5vw, 6px);

          background: linear-gradient(
            90deg,
            rgb(15, 31, 51),
            rgb(26, 58, 92),
            rgb(30, 73, 118)
          );
        }


        /* ====================================================
           CONTENT
        ==================================================== */

        .uc-content {
          width: 100%;
          max-width: 100%;
          min-width: 0;

          box-sizing: border-box;

          padding: clamp(
            10px,
            2vw,
            20px
          );
        }


        /* ====================================================
           HEADER
        ==================================================== */

        .uc-header {
          display: flex;

          align-items: flex-start;
          justify-content: space-between;

          width: 100%;
          max-width: 100%;
          min-width: 0;

          gap: clamp(8px, 1.5vw, 14px);

          margin-bottom: clamp(
            8px,
            1.5vw,
            14px
          );
        }

        .uc-heading {
          min-width: 0;
          max-width: 100%;
          flex: 1 1 auto;
        }

        .uc-name-row {
          display: flex;

          align-items: center;

          min-width: 0;
          max-width: 100%;

          gap: clamp(
            5px,
            1vw,
            8px
          );
        }

        .uc-user-icon {
          flex: 0 0 auto;

          font-size: clamp(
            12px,
            1.5vw,
            16px
          );

          color: rgb(30, 73, 118);
        }

        .uc-name {
          min-width: 0;
          max-width: 100%;

          margin: 0;

          overflow: hidden;

          font-size: clamp(
            14px,
            1.7vw,
            19px
          );

          font-weight: 700;

          line-height: 1.35;

          color: rgb(15, 23, 42);

          text-overflow: ellipsis;

          white-space: nowrap;
        }

        .uc-profession {
          max-width: 100%;

          margin: 4px 0 0;

          overflow: hidden;

          font-size: clamp(
            10px,
            1.2vw,
            13px
          );

          font-weight: 600;

          line-height: 1.4;

          color: rgb(26, 58, 92);

          text-overflow: ellipsis;

          white-space: nowrap;
        }

        .uc-registration {
          max-width: 100%;

          margin: 2px 0 0;

          overflow: hidden;

          font-size: clamp(
            8px,
            1vw,
            11px
          );

          line-height: 1.4;

          color: rgb(148, 163, 184);

          text-overflow: ellipsis;

          white-space: nowrap;
        }

        .uc-registration-label {
          font-weight: 700;
        }


        /* ====================================================
           ONLINE STATUS
        ==================================================== */

        .uc-status {
          display: inline-flex;

          align-items: center;
          justify-content: center;

          flex: 0 1 auto;

          min-width: 0;

          gap: clamp(
            4px,
            0.7vw,
            6px
          );

          padding:
            clamp(4px, 0.7vw, 6px)
            clamp(6px, 1vw, 10px);

          border-radius: 9999px;

          font-size: clamp(
            8px,
            1vw,
            11px
          );

          font-weight: 700;

          line-height: 1.2;

          white-space: nowrap;

          box-sizing: border-box;
        }

        .uc-status-online {
          color: rgb(22, 163, 74);

          background: rgb(240, 253, 244);

          box-shadow:
            inset 0 0 0 1px
            rgb(220, 252, 231);
        }

        .uc-status-offline {
          color: rgb(107, 114, 128);

          background: rgb(243, 244, 246);

          box-shadow:
            inset 0 0 0 1px
            rgb(229, 231, 235);
        }

        .uc-status-dot {
          flex: 0 0 auto;

          font-size: clamp(
            5px,
            0.6vw,
            7px
          );
        }

        .uc-status-online .uc-status-dot {
          color: rgb(34, 197, 94);
        }

        .uc-status-offline .uc-status-dot {
          color: rgb(156, 163, 175);
        }


        /* ====================================================
           MAIN PROFILE AREA
        ==================================================== */

        .uc-main {
          display: flex;

          align-items: flex-start;

          width: 100%;
          max-width: 100%;
          min-width: 0;

          gap: clamp(
            8px,
            1.5vw,
            14px
          );

          margin-top: clamp(
            8px,
            1.5vw,
            14px
          );
        }


        /* ====================================================
           PROFILE WRAPPER
        ==================================================== */

        .uc-profile-wrapper {
          display: flex;

          align-items: flex-start;
          justify-content: center;

          flex: 0 0 clamp(
            58px,
            9vw,
            100px
          );

          width: clamp(
            58px,
            9vw,
            100px
          );

          max-width: 100%;
        }

        .uc-profile-container {
          position: relative;

          width: clamp(
            58px,
            9vw,
            100px
          );

          height: clamp(
            58px,
            9vw,
            100px
          );
        }

        .uc-profile-image {
          display: block;

          width: 100%;
          height: 100%;

          box-sizing: border-box;

          border:
            clamp(1px, 0.2vw, 2px)
            solid
            rgba(30, 73, 118, 0.2);

          border-radius: clamp(
            10px,
            1.5vw,
            16px
          );

          object-fit: cover;

          background: rgb(239, 246, 255);

          box-shadow:
            0 2px 6px
            rgba(0, 0, 0, 0.08);
        }


        /* ====================================================
           ONLINE INDICATOR
        ==================================================== */

        .uc-online-indicator {
          position: absolute;

          right: 1px;
          bottom: 1px;

          width: clamp(
            9px,
            1.2vw,
            13px
          );

          height: clamp(
            9px,
            1.2vw,
            13px
          );

          border:
            clamp(1px, 0.2vw, 2px)
            solid
            #ffffff;

          border-radius: 50%;

          box-sizing: border-box;
        }

        .uc-online {
          background: rgb(34, 197, 94);
        }

        .uc-offline {
          background: rgb(156, 163, 175);
        }


        /* ====================================================
           DETAILS
        ==================================================== */

        .uc-details {
          display: flex;

          flex: 1 1 auto;

          flex-direction: column;

          min-width: 0;
          max-width: 100%;

          gap: clamp(
            4px,
            0.8vw,
            7px
          );

          font-size: clamp(
            10px,
            1.2vw,
            13px
          );

          color: rgb(55, 65, 81);
        }

        .uc-detail-row {
          display: flex;

          align-items: flex-start;

          width: 100%;
          max-width: 100%;
          min-width: 0;

          gap: 4px;

          margin: 0;

          line-height: 1.45;
        }

        .uc-detail-label {
          flex: 0 0 auto;

          font-weight: 700;
        }

        .uc-detail-value {
          min-width: 0;
          max-width: 100%;

          overflow: hidden;

          text-overflow: ellipsis;

          white-space: nowrap;
        }

        .uc-email-label {
          color: rgb(30, 73, 118);
        }

        .uc-phone-label {
          color: rgb(22, 163, 74);
        }


        /* ====================================================
           LOCATION
        ==================================================== */

        .uc-location-row {
          align-items: flex-start;
        }

        .uc-location-icon {
          flex: 0 0 auto;

          margin-top: 2px;

          font-size: clamp(
            9px,
            1vw,
            12px
          );

          color: rgb(236, 72, 153);
        }

        .uc-location-content {
          min-width: 0;
          max-width: 100%;

          overflow: hidden;
        }

        .uc-location-label {
          color: rgb(219, 39, 119);
        }

        .uc-location-value {
          overflow-wrap: anywhere;
          word-break: break-word;
        }


        /* ====================================================
           DISTANCE
        ==================================================== */

        .uc-distance {
          width: 100%;
          max-width: 100%;

          margin: 0;

          overflow-wrap: anywhere;

          font-size: clamp(
            10px,
            1.1vw,
            13px
          );

          font-weight: 700;

          color: rgb(37, 99, 235);
        }


        /* ====================================================
           DESCRIPTION
        ==================================================== */

        .uc-description-box {
          width: 100%;
          max-width: 100%;
          min-width: 0;

          margin-top: clamp(
            9px,
            1.5vw,
            14px
          );

          padding: clamp(
            9px,
            1.5vw,
            14px
          );

          box-sizing: border-box;

          border-radius: clamp(
            9px,
            1.2vw,
            13px
          );

          background: rgb(248, 250, 252);
        }

        .uc-section-title {
          margin: 0 0 4px;

          font-size: clamp(
            10px,
            1.1vw,
            13px
          );

          font-weight: 700;

          color: rgb(26, 58, 92);
        }

        .uc-description {
          max-width: 100%;
          max-height: 112px;

          overflow-y: auto;

          font-size: clamp(
            10px,
            1.1vw,
            13px
          );

          line-height: 1.5;

          color: rgb(75, 85, 99);

          white-space: pre-line;

          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .uc-read-more {
          margin-left: 6px;

          padding: 0;

          border: 0;

          background: transparent;

          color: rgb(37, 99, 235);

          font-size: clamp(
            9px,
            1vw,
            12px
          );

          font-weight: 700;

          text-decoration: underline;

          cursor: pointer;
        }

        .uc-read-more:hover {
          color: rgb(29, 78, 216);
        }


        /* ====================================================
           HEALTH SCHEMES
        ==================================================== */

        .uc-health-schemes {
          display: flex;

          align-items: flex-start;

          width: 100%;
          max-width: 100%;
          min-width: 0;

          gap: clamp(
            6px,
            1vw,
            9px
          );

          margin-top: clamp(
            9px,
            1.5vw,
            14px
          );

          padding: clamp(
            8px,
            1.2vw,
            11px
          );

          box-sizing: border-box;

          border:
            1px solid
            rgb(220, 252, 231);

          border-radius: clamp(
            9px,
            1.2vw,
            13px
          );

          background:
            rgba(240, 253, 244, 0.7);
        }

        .uc-health-icon {
          flex: 0 0 auto;

          margin-top: 2px;

          font-size: clamp(
            11px,
            1.3vw,
            15px
          );

          color: rgb(22, 163, 74);
        }

        .uc-health-text {
          min-width: 0;
          max-width: 100%;

          margin: 0;

          font-size: clamp(
            10px,
            1.1vw,
            13px
          );

          line-height: 1.5;

          color: rgb(21, 128, 61);

          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .uc-health-title {
          font-weight: 700;
        }


        /* ====================================================
           ACTION BUTTONS
        ==================================================== */

        .uc-actions {
          display: grid;

          grid-template-columns:
            repeat(
              auto-fit,
              minmax(
                min(150px, 100%),
                1fr
              )
            );

          gap: clamp(
            6px,
            1vw,
            9px
          );

          width: 100%;
          max-width: 100%;
          min-width: 0;

          margin-top: clamp(
            12px,
            1.8vw,
            17px
          );
        }

        .uc-action {
          display: flex;

          align-items: center;
          justify-content: center;

          width: 100%;
          min-width: 0;

          min-height: clamp(
            38px,
            4vw,
            44px
          );

          gap: clamp(
            4px,
            0.8vw,
            7px
          );

          padding:
            clamp(7px, 1vw, 9px)
            clamp(6px, 1.2vw, 12px);

          box-sizing: border-box;

          border: 0;

          border-radius: clamp(
            9px,
            1.2vw,
            13px
          );

          color: #ffffff;

          font-size: clamp(
            10px,
            1.1vw,
            13px
          );

          font-weight: 700;

          line-height: 1.2;

          text-align: center;

          text-decoration: none;

          cursor: pointer;

          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            background-color 0.2s ease;
        }

        .uc-action span {
          min-width: 0;

          overflow: hidden;

          text-overflow: ellipsis;

          white-space: nowrap;
        }

        .uc-action:hover {
          transform: translateY(-1px);

          box-shadow:
            0 4px 10px
            rgba(0, 0, 0, 0.12);
        }

        .uc-action:active {
          transform: translateY(0);
        }

        .uc-call {
          background: rgb(34, 197, 94);
        }

        .uc-call:hover {
          background: rgb(22, 163, 74);
        }

        .uc-location {
          background: rgb(30, 73, 118);
        }

        .uc-location:hover {
          background: rgb(26, 58, 92);
        }

        .uc-book {
          background: linear-gradient(
            90deg,
            rgb(26, 58, 92),
            rgb(14, 165, 233)
          );
        }


        /* ====================================================
           NARROW CONTAINERS
           Important when UserCard is inside a modal,
           sidebar, grid column, etc.
        ==================================================== */

        @container (max-width: 520px) {

          .uc-header {
            flex-wrap: wrap;
          }

          .uc-status {
            flex: 0 0 auto;
          }

          .uc-main {
            flex-wrap: wrap;
          }

          .uc-profile-wrapper {
            flex: 0 0 clamp(
              58px,
              18vw,
              80px
            );

            width: clamp(
              58px,
              18vw,
              80px
            );
          }

          .uc-profile-container {
            width: clamp(
              58px,
              18vw,
              80px
            );

            height: clamp(
              58px,
              18vw,
              80px
            );
          }
        }


        /* ====================================================
           VERY NARROW SCREEN FALLBACK
        ==================================================== */

        @media (max-width: 380px) {

          .uc-header {
            flex-direction: column;
            align-items: stretch;
          }

          .uc-status {
            align-self: flex-start;
          }

          .uc-main {
            gap: 8px;
          }

          .uc-profile-wrapper {
            flex-basis: 58px;
            width: 58px;
          }

          .uc-profile-container {
            width: 58px;
            height: 58px;
          }

          .uc-detail-value {
            white-space: normal;
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .uc-location-content {
            overflow: visible;
          }

          .uc-actions {
            grid-template-columns: 1fr;
          }
        }


        /* ====================================================
           REDUCE MOTION
        ==================================================== */

        @media (prefers-reduced-motion: reduce) {

          .uc-card,
          .uc-action {
            transition: none;
          }

          .uc-card:hover,
          .uc-action:hover {
            transform: none;
          }
        }

      `}</style>

      {/* ======================================================
          CARD
      ====================================================== */}

      <article className="uc-card">
        {/* ====================================================
            TOP STRIP
        ==================================================== */}

        <div className="uc-top-strip" />

        {/* ====================================================
            CONTENT
        ==================================================== */}

        <div className="uc-content">
          {/* ==================================================
              HEADER
          ================================================== */}

          <div className="uc-header">
            <div className="uc-heading">
              <div className="uc-name-row">
                <FaUserMd className="uc-user-icon" />

                <h3 className="uc-name">{safe(actualUser.fullName)}</h3>
              </div>

              <p className="uc-profession">{safe(actualUser.profession)}</p>

              <p className="uc-registration">
                <span className="uc-registration-label">Regd:</span>{" "}
                {safe(actualUser.registration)}
              </p>
            </div>

            {/* ONLINE STATUS */}

            <div
              className={
                isOnline
                  ? "uc-status uc-status-online"
                  : "uc-status uc-status-offline"
              }
            >
              <FaCircle className="uc-status-dot" />

              <span>{isOnline ? "User is Online" : "User is Offline"}</span>
            </div>
          </div>

          {/* ==================================================
              PROFILE + DETAILS
          ================================================== */}

          <div className="uc-main">
            {/* PROFILE */}

            <div className="uc-profile-wrapper">
              <div className="uc-profile-container">
                <img
                  src={profileImage}
                  alt={actualUser?.fullName || "Profile"}
                  className="uc-profile-image"
                  loading="lazy"
                  onError={handleImageError}
                />

                <span
                  className={
                    isOnline
                      ? "uc-online-indicator uc-online"
                      : "uc-online-indicator uc-offline"
                  }
                  title={isOnline ? "User is Online" : "User is Offline"}
                />
              </div>
            </div>

            {/* USER DETAILS */}

            <div className="uc-details">
              {/* EMAIL */}

              <p className="uc-detail-row">
                <span
                  className="
                    uc-detail-label
                    uc-email-label
                  "
                >
                  Email:
                </span>

                <span className="uc-detail-value">
                  {safe(actualUser.email)}
                </span>
              </p>

              {/* PHONE */}

              <p className="uc-detail-row">
                <span
                  className="
                    uc-detail-label
                    uc-phone-label
                  "
                >
                  Phone:
                </span>

                <span className="uc-detail-value">
                  {safe(actualUser.phone)}
                </span>
              </p>

              {/* LOCATION */}

              <p
                className="
                  uc-detail-row
                  uc-location-row
                "
              >
                <FaMapMarkerAlt className="uc-location-icon" />

                <span className="uc-location-content">
                  <span
                    className="
                      uc-detail-label
                      uc-location-label
                    "
                  >
                    Location:
                  </span>{" "}
                  <span className="uc-location-value">
                    {safe(actualUser.location)}
                  </span>
                </span>
              </p>

              {/* DISTANCE */}

              {typeof actualUser.distanceKm === "number" && (
                <p className="uc-distance">
                  🚀 {actualUser.distanceKm.toFixed(2)} km away
                </p>
              )}
            </div>
          </div>

          {/* ==================================================
              DESCRIPTION
          ================================================== */}

          {actualUser.description && (
            <div className="uc-description-box">
              <p className="uc-section-title">Description</p>

              <DescriptionBlock description={actualUser.description} />
            </div>
          )}

          {/* ==================================================
              HEALTH SCHEMES
          ================================================== */}

          {Array.isArray(actualUser.healthSchemes) &&
            actualUser.healthSchemes.length > 0 && (
              <div className="uc-health-schemes">
                <FaShieldAlt className="uc-health-icon" />

                <p className="uc-health-text">
                  <span className="uc-health-title">Health Schemes:</span>{" "}
                  {actualUser.healthSchemes.join(", ")}
                </p>
              </div>
            )}

          {/* ==================================================
              ACTIONS
          ================================================== */}

          <div className="uc-actions">
            {/* CALL */}

            {actualUser.phone && (
              <a
                href={`tel:${actualUser.phone}`}
                className="
                  uc-action
                  uc-call
                "
              >
                <FaPhoneAlt />

                <span>Call</span>
              </a>
            )}

            {/* LOCATION */}

            {actualUser.googleMapLink && (
              <a
                href={actualUser.googleMapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  uc-action
                  uc-location
                "
              >
                <FaMapMarkerAlt />

                <span>Location</span>
              </a>
            )}

            {/* BOOK APPOINTMENT */}

            <button
              type="button"
              onClick={handleBookAppointment}
              className="
                uc-action
                uc-book
              "
            >
              <FaCalendarCheck />

              <span>
                {isAuthenticated ? "Book Appointment" : "Login to Book"}
              </span>
            </button>
          </div>
        </div>
      </article>
    </>
  );
};

export default UserCard;
